import React, { useEffect, useMemo, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Pagination,
  Paper,
  Select,
  Skeleton,
  Stack,
  TextField,
  Button,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme
} from '@mui/material';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import ArrowDownwardRoundedIcon from '@mui/icons-material/ArrowDownwardRounded';
import ArrowUpwardRoundedIcon from '@mui/icons-material/ArrowUpwardRounded';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import FilterBar from '../../components/pedidos/FilterBar';
import GroupedTable from '../../components/pedidos/GroupedTable';
import GroupCards from '../../components/pedidos/GroupCards';
import DetailItemsTable from '../../components/pedidos/DetailItemsTable';
import DashboardCharts from '../../components/pedidos/DashboardCharts';
import { useMiContexto } from '../../components/base/ContextProvider';
import { useCurrentUser } from '../../components/base/UserProvider';
import OrdersDashboardService from '../../services/ordersDashboard';
import OrdersService from '../../services/orders';
import SaleService from '../../services/sales';
import ArticuloService from '../../services/articulos';
import { OPERATION, STATUS_SALE } from '../../helpers/enums';
import { mapOrdersDashboardResponse, flattenLeafGroups } from './dashboard/adapter';
import { filterGroups, filterSortItems, flattenItems, paginate, sortGroupsTree, summarizeGroups, collectTopGroups } from './dashboard/selectors';
import { stringifyGroupBy } from './dashboard/groupBy';
import { buildChecklistItemKey, summarizeChecklistForItems } from './dashboard/checklist';
import { useOrdersDashboardFilters } from './dashboard/useOrdersDashboardFilters';

const CHECKLIST_STORAGE_KEY = 'orders-dashboard-checklist-v1';

const GROUP_SORT_OPTIONS = [
  { value: 'key', label: 'Clave' },
  { value: 'count', label: 'Pedidos' },
  { value: 'total_quantity', label: 'Unidades' }
];

const StatCard = ({ title, value, subtitle, color }) => (
  <Paper
    elevation={0}
    sx={{
      p: 1.2,
      borderRadius: 2,
      border: '1px solid #e5e7eb',
      minWidth: 0,
      flex: 1,
      background: `linear-gradient(130deg, ${color}14 0%, #ffffff 100%)`
    }}
  >
    <Typography variant='caption' sx={{ color: 'text.secondary', fontWeight: 700 }}>{title}</Typography>
    <Typography sx={{ fontWeight: 800, fontSize: '1.3rem', color }}>{Number(value || 0).toLocaleString('es-AR')}</Typography>
    {subtitle && <Typography variant='caption' sx={{ color: 'text.secondary' }}>{subtitle}</Typography>}
  </Paper>
);

const LoadingSkeleton = () => (
  <Stack spacing={1}>
    <Skeleton variant='rounded' height={100} />
    <Skeleton variant='rounded' height={220} />
    <Skeleton variant='rounded' height={220} />
  </Stack>
);

const DashboardOrders = ({ setOptions, setChildHeader }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { miValor } = useMiContexto();
  const { currentUser } = useCurrentUser();
  const stores = useMemo(() => (Array.isArray(miValor) ? miValor : []), [miValor]);

  const { state, apiFilters, actions } = useOrdersDashboardFilters();
  const [isFetching, setIsFetching] = useState(false);
  const [error, setError] = useState('');
  const [dashboard, setDashboard] = useState({ group_by: 'client', group_fields: ['client'], groups: [] });
  const [checklistByScope, setChecklistByScope] = useState({});
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [confirmingPurchases, setConfirmingPurchases] = useState(false);
  const [purchaseName, setPurchaseName] = useState('');
  const [purchaseError, setPurchaseError] = useState('');
  const [purchaseSuccess, setPurchaseSuccess] = useState('');

  const resolvedStoreId = useMemo(() => state.store_id || stores[0]?._id || '', [state.store_id, stores]);

  useEffect(() => {
    try {
      const raw = window.localStorage.getItem(CHECKLIST_STORAGE_KEY);
      setChecklistByScope(raw ? JSON.parse(raw) : {});
    } catch (storageError) {
      setChecklistByScope({});
    }
  }, []);

  const persistChecklist = (next) => {
    setChecklistByScope(next);
    try {
      window.localStorage.setItem(CHECKLIST_STORAGE_KEY, JSON.stringify(next));
    } catch (storageError) {
      console.warn('No se pudo guardar checklist', storageError);
    }
  };

  useEffect(() => {
    setOptions && setOptions([]);
    setChildHeader && setChildHeader(<></>);
  }, [setChildHeader, setOptions]);

  useEffect(() => {
    if (!state.store_id && stores[0]?._id) {
      actions.patch({ store_id: stores[0]._id });
    }
  }, [actions, state.store_id, stores]);

  const loadDashboard = async () => {
    if (!resolvedStoreId) return;
    if (state.date_from && state.date_to && state.date_from > state.date_to) {
      setError('El rango de fechas es inválido: la fecha desde no puede ser mayor a la fecha hasta.');
      return;
    }

    try {
      setIsFetching(true);
      setError('');
      const response = await OrdersDashboardService.getDashboard(resolvedStoreId, {
        group_by: apiFilters.group_by,
        status: apiFilters.status,
        search: apiFilters.search,
        date_from: apiFilters.date_from,
        date_to: apiFilters.date_to
      });
      setDashboard(mapOrdersDashboardResponse(response, state.group_fields));
    } catch (requestError) {
      setError(requestError?.message || 'Error cargando dashboard de pedidos.');
      // Keep previous successful data mounted to avoid UI flicker while notifying the error.
      if (!dashboard.groups?.length) {
        setDashboard({ group_by: stringifyGroupBy(state.group_fields), group_fields: state.group_fields, groups: [] });
      }
    } finally {
      setIsFetching(false);
    }
  };

  useEffect(() => {
    loadDashboard();
  }, [resolvedStoreId, apiFilters.group_by, apiFilters.status, apiFilters.search, apiFilters.date_from, apiFilters.date_to]); // eslint-disable-line react-hooks/exhaustive-deps

  const filteredTree = useMemo(
    () => filterGroups(dashboard.groups, { status: state.status, search: apiFilters.search }),
    [dashboard.groups, state.status, apiFilters.search]
  );

  const sortedTree = useMemo(
    () => sortGroupsTree(filteredTree, state.sort_by, state.sort_dir),
    [filteredTree, state.sort_by, state.sort_dir]
  );

  const summary = useMemo(() => summarizeGroups(sortedTree), [sortedTree]);
  const topGroups = useMemo(() => collectTopGroups(flattenLeafGroups(sortedTree), 10), [sortedTree]);

  const groupPagination = useMemo(
    () => paginate(sortedTree, state.page, state.page_size),
    [sortedTree, state.page, state.page_size]
  );

  const pagedGroups = groupPagination.items;

  const leafGroups = useMemo(() => flattenLeafGroups(sortedTree), [sortedTree]);
  const selectedLeafById = useMemo(
    () => leafGroups.find((leaf) => leaf.id === state.leaf) || null,
    [leafGroups, state.leaf]
  );

  const effectiveSelectedLeaf = useMemo(
    () => selectedLeafById || leafGroups[0] || null,
    [selectedLeafById, leafGroups]
  );

  const currentChecklistScope = useMemo(
    () => `${resolvedStoreId || 'none'}::${effectiveSelectedLeaf?.id || 'none'}`,
    [resolvedStoreId, effectiveSelectedLeaf?.id]
  );

  const selectedItems = useMemo(() => {
    if (!effectiveSelectedLeaf) return [];
    return filterSortItems(flattenItems(effectiveSelectedLeaf), {
      search: state.item_search,
      sortBy: state.item_sort_by,
      sortDir: state.item_sort_dir
    }).map((item) => ({
      ...item,
      _rowKey: buildChecklistItemKey(item)
    }));
  }, [effectiveSelectedLeaf, state.item_search, state.item_sort_by, state.item_sort_dir]);

  const detailItems = useMemo(
    () => selectedItems,
    [selectedItems]
  );

  const statusOptions = useMemo(
    () => Object.keys(summary.statuses || {}).sort((a, b) => a.localeCompare(b)),
    [summary.statuses]
  );

  const leafClientList = useMemo(
    () => (leafGroups || []).map((leaf) => {
      const scope = `${resolvedStoreId || 'none'}::${leaf?.id || 'none'}`;
      const scopeChecklist = checklistByScope[scope] || {};
      const itemsForCompletion = flattenItems(leaf).map((item) => ({ ...item, _rowKey: buildChecklistItemKey(item) }));
      const checklistState = summarizeChecklistForItems(itemsForCompletion, scopeChecklist);

      return {
        ...leaf,
        clientLabel: leaf?.clients?.[0] || leaf?.key || 'Cliente sin nombre',
        previewItems: (leaf?.items || []).slice(0, 4),
        checklistState
      };
    }),
    [leafGroups, resolvedStoreId, checklistByScope]
  );

  const currentChecklist = useMemo(
    () => checklistByScope[currentChecklistScope] || {},
    [checklistByScope, currentChecklistScope]
  );

  const checklistSummary = useMemo(
    () => summarizeChecklistForItems(selectedItems, currentChecklist),
    [currentChecklist, selectedItems]
  );

  const isCompletedStatus = (status) => {
    const normalized = String(status || '').trim().toLowerCase();
    return normalized === 'completed' || normalized === 'closed' || normalized === 'cerrado';
  };

  const lockedItemKeys = useMemo(() => {
    return new Set(
      detailItems
        .filter((item) => isCompletedStatus(item?.status))
        .map((item) => item._rowKey)
    );
  }, [detailItems]);

  const updateChecklistForItem = (item, updater) => {
    const key = item?._rowKey;
    if (!key) return;
    if (lockedItemKeys.has(key)) return;
    const currentEntry = currentChecklist[key] || { received: 0, checked: false };
    const nextEntry = updater(currentEntry);
    const nextScope = {
      ...currentChecklist,
      [key]: nextEntry
    };
    persistChecklist({
      ...checklistByScope,
      [currentChecklistScope]: nextScope
    });
  };

  const handleToggleChecked = (item) => {
    updateChecklistForItem(item, (entry) => ({
      ...entry,
      checked: !entry.checked,
      received: !entry.checked ? Math.max(entry.received, Number(item.quantity || 0)) : entry.received
    }));
  };

  const handleIncrementReceived = (item) => {
    const max = Number(item.quantity || 0);
    updateChecklistForItem(item, (entry) => {
      const received = Math.min(max, Number(entry.received || 0) + 1);
      return {
        ...entry,
        received,
        checked: received >= max && max > 0
      };
    });
  };

  const handleDecrementReceived = (item) => {
    updateChecklistForItem(item, (entry) => {
      const received = Math.max(0, Number(entry.received || 0) - 1);
      return {
        ...entry,
        received,
        checked: received >= Number(item.quantity || 0) && Number(item.quantity || 0) > 0
      };
    });
  };

  const handleMarkPageComplete = () => {
    const nextScope = { ...currentChecklist };
    detailItems.forEach((item) => {
      if (lockedItemKeys.has(item._rowKey)) return;
      nextScope[item._rowKey] = {
        received: Number(item.quantity || 0),
        checked: true
      };
    });
    persistChecklist({
      ...checklistByScope,
      [currentChecklistScope]: nextScope
    });
  };

  const handleClearPageChecklist = () => {
    const nextScope = { ...currentChecklist };
    detailItems.forEach((item) => {
      if (lockedItemKeys.has(item._rowKey)) return;
      delete nextScope[item._rowKey];
    });
    persistChecklist({
      ...checklistByScope,
      [currentChecklistScope]: nextScope
    });
  };

  const checkedItems = useMemo(() => {
    return detailItems.filter((item) => {
      const entry = currentChecklist[item._rowKey];
      return Number(entry?.received || 0) > 0 && !lockedItemKeys.has(item._rowKey);
    });
  }, [detailItems, currentChecklist, lockedItemKeys]);

  const isLeafCompleted = useMemo(() => {
    if (!detailItems.length) return false;
    return detailItems.every((item) => isCompletedStatus(item?.status));
  }, [detailItems]);

  const extractOrderId = (item) => {
    return item?.order_id || item?.orderId || item?.idOrder || item?.raw?.order_id || item?.raw?.orderId || item?.raw?.idOrder || item?.raw?.order?._id || '';
  };

  const getCategoryName = (article) => {
    const category = article?.category;
    if (!category) return '';
    if (typeof category === 'string') return category;
    return category?.name || '';
  };

  const resolvePriceByCategory = (article, totalConfirmedUnits = 0) => {
    const categoryName = getCategoryName(article).toLowerCase();
    const isAccessory = categoryName.includes('accesorio');

    const unitPrice = Number(article?.prices?.sale_price || article?.prices?.unit || 0);
    const applyWholesale = !isAccessory && totalConfirmedUnits > 2;
    const wholesalePrice = Math.max(0, unitPrice - 200);

    return {
      salePrice: applyWholesale ? wholesalePrice : unitPrice,
      listPrice: Number(article?.prices?.list_price || unitPrice || wholesalePrice || 0)
    };
  };

  const openConfirmPurchases = () => {
    const defaultName = `Compra ${effectiveSelectedLeaf?.clients?.[0] || effectiveSelectedLeaf?.key || 'Cliente'}`;
    setPurchaseName(defaultName);
    setPurchaseError('');
    setConfirmDialogOpen(true);
  };

  const handleConfirmPurchases = async () => {
    if (!resolvedStoreId) return;
    if (!purchaseName.trim()) {
      setPurchaseError('Debes indicar un nombre para el armado.');
      return;
    }
    if (!checkedItems.length) {
      setPurchaseError('No hay ítems checklistados para confirmar.');
      return;
    }

    setConfirmingPurchases(true);
    setPurchaseError('');

    try {
      const uniqueSkus = Array.from(new Set(checkedItems.map((item) => item.sku).filter(Boolean)));

      const articleLookupEntries = await Promise.all(uniqueSkus.map(async (sku) => {
        const result = await ArticuloService.searchArticlesInStore(resolvedStoreId, sku, OPERATION.VENDER, false);
        const articles = Array.isArray(result) ? result : [];
        const exact = articles.find((article) => String(article?.sku || '').toLowerCase() === String(sku).toLowerCase());
        return [sku, exact || articles[0] || null];
      }));

      const articleBySku = Object.fromEntries(articleLookupEntries);
      const totalConfirmedUnits = checkedItems.reduce((acc, item) => {
        const entry = currentChecklist[item._rowKey] || { received: 0 };
        return acc + Math.max(0, Number(entry.received || 0));
      }, 0);

      const salePayload = checkedItems.map((item) => {
        const article = articleBySku[item.sku];
        if (!article) {
          throw new Error(`No se encontró artículo para SKU ${item.sku}`);
        }

        const entry = currentChecklist[item._rowKey] || { received: 0 };
        const quantitySold = Math.max(1, Number(entry.received || item.quantity || 1));
        const priceData = resolvePriceByCategory(article, totalConfirmedUnits);

        return {
          store_id: resolvedStoreId,
          article_id: article.article_id || article._id,
          sku: article.sku || item.sku,
          quantity_in_stock: Number(article.quantity || 0),
          quantity_sold: quantitySold,
          prices_sold: {
            list_price: priceData.listPrice,
            sale_price: priceData.salePrice
          },
          prices: {
            list_price: priceData.listPrice,
            sale_price: Number(article?.prices?.sale_price || priceData.salePrice),
            wholesale: Number(article?.prices?.wholesale || priceData.salePrice),
            unit: Number(article?.prices?.unit || article?.prices?.sale_price || priceData.salePrice)
          },
          name: article?.article?.name || item.description || item.sku,
          description: article?.article?.description || item.description || '',
          image: article?.article?.images?.[0] || '',
          type_detail: article?.type_detail || [],
          color_selected: item.variant || '',
          size_selected: item.size || '',
          status: STATUS_SALE.RESERVED,
          nameSale: purchaseName.trim(),
          userId: currentUser?._id,
          deposit: 0
        };
      });

      await SaleService.save(resolvedStoreId, salePayload);

      const uniqueOrderIds = Array.from(new Set(checkedItems.map(extractOrderId).filter(Boolean)));
      if (uniqueOrderIds.length) {
        await OrdersService.updateStatus(
          resolvedStoreId,
          'completed',
          uniqueOrderIds.map((id) => ({ _id: id, idOrder: id, order_id: id }))
        );
      }

      setPurchaseSuccess(`Compras confirmadas (${salePayload.length} ítems).`);
      setConfirmDialogOpen(false);
      await loadDashboard();
    } catch (confirmError) {
      setPurchaseError(confirmError?.message || 'No se pudo confirmar la compra.');
    } finally {
      setConfirmingPurchases(false);
    }
  };

  const handleSelectLeaf = (group) => {
    const leafId = group?.id || group?.keyPath || group?.key;
    actions.setLeafSelection(leafId);
  };

  const hasDashboardData = groupPagination.total > 0;
  const showInitialLoading = isFetching && !hasDashboardData;

  return (
    <Box sx={{ p: { xs: 0.8, sm: 2 }, display: 'flex', flexDirection: 'column', gap: 1.2, pb: { xs: 8, sm: 2 } }}>
      <Paper
        elevation={0}
        sx={{
          borderRadius: 3,
          border: '1px solid #d4d4d8',
          p: { xs: 1.2, sm: 1.8 },
          background: 'radial-gradient(circle at 14% 16%, #fef3c7 0%, #ffffff 40%), radial-gradient(circle at 84% 14%, #dbeafe 0%, #ffffff 45%)'
        }}
      >
        <Stack direction='row' justifyContent='space-between' alignItems='center' gap={1} flexWrap='wrap'>
          <Box>
            <Typography sx={{ fontWeight: 900, fontSize: { xs: '1.2rem', sm: '1.4rem' } }}>
              Dashboard operativo de pedidos
            </Typography>
            <Typography variant='body2' sx={{ color: 'text.secondary' }}>
              Analiza por cliente, SKU, color, talle y estado con agrupaciones jerárquicas.
            </Typography>
          </Box>

          <Stack direction='row' gap={1} alignItems='center'>
            <Tooltip title='Refrescar datos'>
              <span>
                <IconButton color='primary' onClick={loadDashboard} disabled={isFetching || !resolvedStoreId}>
                  <RefreshRoundedIcon />
                </IconButton>
              </span>
            </Tooltip>
          </Stack>
        </Stack>
      </Paper>

      {isFetching && hasDashboardData && (
        <Alert severity='info' sx={{ py: 0.2 }}>
          Actualizando datos sin interrumpir tu vista.
        </Alert>
      )}

      {!resolvedStoreId && (
        <Alert severity='warning'>Selecciona una tienda para cargar el dashboard de órdenes.</Alert>
      )}

      <FilterBar
        stores={stores}
        state={state}
        statusOptions={statusOptions}
        onPatch={actions.patch}
        onClear={actions.clearFilters}
        onSetGroupFields={actions.setGroupFields}
        onSetView={actions.setView}
        onQuickView={actions.setGroupFields}
      />

      <Stack direction={{ xs: 'column', md: 'row' }} gap={1}>
        <StatCard title='Total de grupos' value={summary.groupCount} subtitle='incluye subgrupos' color='#0f766e' />
        <StatCard title='Total de pedidos' value={summary.orderCount} subtitle='conteo agregado' color='#1d4ed8' />
        <StatCard title='Total de unidades' value={summary.totalQuantity} subtitle='sumatoria de quantities' color='#b45309' />
      </Stack>

      <Paper elevation={0} sx={{ p: 1, borderRadius: 2, border: '1px solid #e5e7eb' }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent='space-between' alignItems={{ xs: 'stretch', sm: 'center' }} gap={1}>
          <Stack direction='row' gap={1} alignItems='center'>
            <FormControl size='small' sx={{ minWidth: 140 }}>
              <InputLabel id='group-sort-label'>Orden grupos</InputLabel>
              <Select
                labelId='group-sort-label'
                value={state.sort_by}
                label='Orden grupos'
                onChange={(event) => actions.setMainSorting(event.target.value, state.sort_dir)}
              >
                {GROUP_SORT_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <Tooltip title='Invertir orden'>
              <IconButton
                color='primary'
                onClick={() => actions.setMainSorting(state.sort_by, state.sort_dir === 'asc' ? 'desc' : 'asc')}
              >
                {state.sort_dir === 'asc' ? <ArrowUpwardRoundedIcon /> : <ArrowDownwardRoundedIcon />}
              </IconButton>
            </Tooltip>
          </Stack>

          <Typography variant='caption' sx={{ color: 'text.secondary' }}>
            Mostrando {groupPagination.items.length} de {groupPagination.total} grupos
          </Typography>
        </Stack>
      </Paper>

      {showInitialLoading && <LoadingSkeleton />}

      {!!error && <Alert severity='error'>{error}</Alert>}

      {!showInitialLoading && !error && groupPagination.total === 0 && (
        <Paper elevation={0} sx={{ p: 3, borderRadius: 2, border: '1px dashed #d4d4d8', textAlign: 'center' }}>
          <Typography sx={{ fontWeight: 700 }}>No hay resultados para los filtros actuales.</Typography>
          <Typography variant='body2' sx={{ color: 'text.secondary' }}>
            Prueba otra combinación de estado, fecha, búsqueda o agrupación.
          </Typography>
        </Paper>
      )}

      {!error && groupPagination.total > 0 && state.view === 'summary' && (
        <Stack spacing={1}>
          <DashboardCharts groups={topGroups} statuses={summary.statuses} />
          <Paper elevation={0} sx={{ borderRadius: 2, border: '1px solid #e5e7eb', p: 1.2 }}>
            <Typography sx={{ fontWeight: 700, mb: 1 }}>Top grupos</Typography>
            <Stack direction='row' gap={0.7} flexWrap='wrap'>
              {topGroups.map((group) => (
                <Chip
                  key={group.id}
                  label={`${group.key}: ${group.total_quantity} uds`}
                  onClick={() => handleSelectLeaf(group)}
                  color='primary'
                  variant='outlined'
                />
              ))}
            </Stack>
          </Paper>
        </Stack>
      )}

      {!error && groupPagination.total > 0 && state.view === 'table' && (
        <GroupedTable groups={pagedGroups} onSelectLeaf={handleSelectLeaf} />
      )}

      {!error && groupPagination.total > 0 && state.view === 'cards' && (
        <GroupCards groups={pagedGroups} onSelect={handleSelectLeaf} />
      )}

      {!error && state.view === 'detail' && (
        <Stack spacing={1}>
          <Paper elevation={0} sx={{ p: 1.2, borderRadius: 2, border: '1px solid #e5e7eb' }}>
            <Typography sx={{ fontWeight: 800, mb: 1 }}>Clientes y contenido pedido</Typography>
            <Typography variant='caption' sx={{ color: 'text.secondary', mb: 0.8, display: 'block' }}>
              Expandí un cliente para revisar rápidamente lo que pidió. Al expandir, se carga su detalle editable abajo.
            </Typography>

            <Box sx={{ maxHeight: { xs: 320, sm: 360 }, overflowY: 'auto', pr: 0.2 }}>
              {leafClientList.map((leaf) => {
                const expanded = effectiveSelectedLeaf?.id === leaf.id;
                const complete = Boolean(leaf?.checklistState?.isComplete);
                return (
                  <Accordion
                    key={leaf.id}
                    expanded={expanded}
                    onChange={() => actions.setLeafSelection(leaf.id)}
                    disableGutters
                    elevation={0}
                    sx={{
                      mb: 0.7,
                      borderRadius: 2,
                      border: complete
                        ? '1px solid #86efac'
                        : expanded
                          ? '1px solid #93c5fd'
                          : '1px solid #e5e7eb',
                      background: complete ? 'linear-gradient(120deg, #f0fdf4 0%, #ffffff 60%)' : '#fff',
                      '&:before': { display: 'none' },
                      overflow: 'hidden'
                    }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreRoundedIcon />}
                      sx={{
                        minHeight: 42,
                        '& .MuiAccordionSummary-content': { my: 0.8 }
                      }}
                    >
                      <Stack direction='row' justifyContent='space-between' alignItems='center' sx={{ width: '100%' }}>
                        <Typography sx={{ fontWeight: 700, pr: 1 }} noWrap title={leaf.clientLabel}>
                          {leaf.clientLabel}
                        </Typography>
                        <Stack direction='row' gap={0.5}>
                          <Chip
                            size='small'
                            label={`${leaf.count} pedidos`}
                            color={complete ? 'success' : expanded ? 'primary' : 'default'}
                          />
                          <Chip size='small' label={`${leaf.total_quantity} uds`} variant='outlined' />
                          <Chip
                            size='small'
                            label={`${leaf.checklistState.completedItems}/${leaf.checklistState.totalItems}`}
                            color={complete ? 'success' : 'default'}
                            variant={complete ? 'filled' : 'outlined'}
                          />
                        </Stack>
                      </Stack>
                    </AccordionSummary>
                    <AccordionDetails sx={{ pt: 0.2 }}>
                      <Stack direction='row' gap={0.6} flexWrap='wrap'>
                        {leaf.previewItems.length === 0 && (
                          <Chip size='small' variant='outlined' label='Sin ítems en vista previa' />
                        )}
                        {leaf.previewItems.map((item, idx) => (
                          <Chip
                            key={`${leaf.id}-preview-${idx}`}
                            size='small'
                            variant='outlined'
                            label={`${Number(item.quantity || 0)}x ${item.description || item.sku || 'Ítem'} (${item.variant || '-'} / ${item.size || '-'})`}
                          />
                        ))}
                        {(leaf.items || []).length > 4 && (
                          <Chip size='small' label={`+${leaf.items.length - 4} más`} />
                        )}
                      </Stack>
                    </AccordionDetails>
                  </Accordion>
                );
              })}
            </Box>

            {effectiveSelectedLeaf && (
              <Stack direction='row' gap={0.6} flexWrap='wrap' mt={0.8}>
                <Chip
                  label={`Seleccionado: ${effectiveSelectedLeaf.clients?.[0] || effectiveSelectedLeaf.key}`}
                  color={checklistSummary.isComplete ? 'success' : 'primary'}
                />
                <Chip label={`Pedidos: ${effectiveSelectedLeaf.count}`} variant='outlined' />
                <Chip label={`Unidades: ${effectiveSelectedLeaf.total_quantity}`} variant='outlined' />
                {checklistSummary.isComplete && <Chip label='Checklist completo' color='success' />}
              </Stack>
            )}
          </Paper>

          <DetailItemsTable
            title={effectiveSelectedLeaf ? `Detalle de ${effectiveSelectedLeaf.id}` : 'Detalle de ítems'}
            items={selectedItems}
            paged={{
              items: detailItems,
              total: detailItems.length,
              page: 1,
              totalPages: 1
            }}
            itemSearch={state.item_search}
            itemSortBy={state.item_sort_by}
            itemSortDir={state.item_sort_dir}
            onPatch={actions.setItemControls}
            statuses={effectiveSelectedLeaf?.statuses || {}}
            checklist={currentChecklist}
            checklistSummary={checklistSummary}
            onToggleChecked={handleToggleChecked}
            onIncrementReceived={handleIncrementReceived}
            onDecrementReceived={handleDecrementReceived}
            onMarkPageComplete={handleMarkPageComplete}
            onClearPage={handleClearPageChecklist}
            onConfirmPurchases={openConfirmPurchases}
            confirmingPurchases={confirmingPurchases}
            lockedItemKeys={lockedItemKeys}
            readOnlyChecklist={isLeafCompleted}
          />

          {isMobile && (
            <Paper
              elevation={3}
              sx={{
                position: 'fixed',
                left: 8,
                right: 8,
                bottom: 8,
                zIndex: 1200,
                p: 1,
                borderRadius: 2,
                border: '1px solid #dbeafe',
                background: 'rgba(255,255,255,0.96)',
                backdropFilter: 'blur(8px)'
              }}
            >
              <Stack direction='row' justifyContent='space-between' alignItems='center'>
                <Typography variant='caption' sx={{ fontWeight: 700 }}>
                  Checklist: {checklistSummary.completedItems}/{checklistSummary.totalItems} completos
                </Typography>
                <Chip
                  size='small'
                  color='primary'
                  label={`${checklistSummary.received}/${checklistSummary.ordered} uds`}
                />
              </Stack>
            </Paper>
          )}
        </Stack>
      )}

      {!error && groupPagination.total > 0 && (
        <Paper elevation={0} sx={{ p: 1, borderRadius: 2, border: '1px solid #e5e7eb' }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent='space-between' alignItems={{ xs: 'flex-start', sm: 'center' }} gap={1}>
            <Typography variant='caption' sx={{ color: 'text.secondary' }}>
              Página {groupPagination.page} de {groupPagination.totalPages}
            </Typography>
            <Pagination
              color='primary'
              page={groupPagination.page}
              count={groupPagination.totalPages}
              onChange={(_, nextPage) => actions.setPagination(nextPage, state.page_size)}
              size='small'
            />
          </Stack>
        </Paper>
      )}

      {showInitialLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}>
          <CircularProgress size={22} />
        </Box>
      )}

      {!!purchaseSuccess && (
        <Alert severity='success' onClose={() => setPurchaseSuccess('')}>
          {purchaseSuccess}
        </Alert>
      )}

      <Dialog open={confirmDialogOpen} onClose={() => !confirmingPurchases && setConfirmDialogOpen(false)} fullWidth maxWidth='xs'>
        <DialogTitle>Confirmar compras</DialogTitle>
        <DialogContent>
          <Stack spacing={1.2} sx={{ mt: 0.4 }}>
            <Typography variant='body2' sx={{ color: 'text.secondary' }}>
              Se armará un pedido interno con lo efectivamente conseguido (aunque sea parcial) y luego se marcarán las órdenes relacionadas como completed.
            </Typography>
            {isLeafCompleted && (
              <Alert severity='info'>Este cliente ya está en estado completed. Solo vista de lectura.</Alert>
            )}
            <TextField
              autoFocus
              size='small'
              label='Nombre del pedido/compra'
              value={purchaseName}
              onChange={(event) => setPurchaseName(event.target.value)}
              disabled={confirmingPurchases}
            />
            <Typography variant='caption' sx={{ color: 'text.secondary' }}>
              Regla de precio: si hay más de 2 unidades confirmadas, se descuenta $200 por unidad para categorías distintas de Accesorios. Con 1 (o hasta 2) se cobra por unidad.
            </Typography>
            {!!purchaseError && <Alert severity='error'>{purchaseError}</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialogOpen(false)} disabled={confirmingPurchases}>Cancelar</Button>
          <Button variant='contained' color='success' onClick={handleConfirmPurchases} disabled={confirmingPurchases || !checkedItems.length || isLeafCompleted}>
            {confirmingPurchases ? 'Procesando...' : `Confirmar (${checkedItems.length})`}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DashboardOrders;
